/** Hook for authentication logic */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth';
import { showError, showSuccess } from '../utils/helpers';

export const useAuth = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(authService.getCurrentUser());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const login = useCallback(async (username, password) => {
    setLoading(true);
    setError(null);

    try {
      const data = await authService.login(username, password);

      if (data.token && data.user) {
        setUser(data.user);
        showSuccess(`Welcome back, ${data.user.fullName}!`);

        // Redirect based on role
        const roleRoutes = {
          super_admin: '/superadmin',
          manager: '/manager',
          supervisor: '/supervisor',
          helpdesk_agent: '/helpdesk'
        };
        navigate(roleRoutes[data.user.role] || '/helpdesk');
      } else {
        const msg = data.error || 'Login failed';
        showError(msg);
        setError(msg);
      }
    } catch (err) {
      const msg = 'Connection error. Please try again.';
      showError(msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await authService.logout();
      setUser(null);
      showSuccess('Logged out successfully');
      navigate('/login');
    } catch (err) {
      showError('Logout error');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const getCurrentUser = useCallback(() => {
    return authService.getCurrentUser();
  }, []);

  const getUserRole = useCallback(() => {
    return authService.getUserRole();
  }, []);

  const hasPermission = useCallback((permission) => {
    return authService.hasPermission(permission);
  }, []);

  return {
    user,
    loading,
    error,
    login,
    logout,
    getCurrentUser,
    getUserRole,
    hasPermission,
    isAuthenticated: authService.isAuthenticated()
  };
};

export default useAuth;
